import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  FieldTemplate,
  SelectFieldDisplayComponent,
} from 'api/models/FieldTemplate';
import AliasInput from 'components/AliasInput/AliasInput';
import { useTranslation } from 'react-i18next';
import { FieldTemplateFormProps } from '.';
import CodeLibraryAutocomplete from './components/CodeLibraryAutocomplete';
import SelectDefaultValueField from './components/SelectDefaultValueField';
import { fieldsReferingToCurrentField } from 'api';
import { GeneralIds } from 'types/types';
import { useParams } from 'react-router-dom';
import useRequest from 'hooks/useRequest';
import { getUsedByFieldsLabel, isCodeLibrary } from './utils';
import SelectOptionsEditor from '../components/SelectOptions';

interface SelectFieldTemplateProps extends FieldTemplateFormProps {
  field: FieldTemplate;
}

export function SelectFieldTemplate({
  formik,
  disabled,
  field,
  isRepeatingFieldsEnabled,
}: SelectFieldTemplateProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  const {
    label,
    isMultiSelect,
    alias,
    optionsSourceCodeLibraryId,
    component,
    description,
    optionsSourceFieldAlias,
  } = formik.values;
  const { id: fieldId } = field;

  const { data: usedByFields } = useRequest<FieldTemplate[]>(
    fieldsReferingToCurrentField,
    {
      dependencies: [optionsSourceFieldAlias],
    },
    [
      {
        projectId,
        alias: optionsSourceFieldAlias ? optionsSourceFieldAlias : alias,
      },
    ],
  );

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={`${t('forms.fieldAttributes.label')} *`}
            type="text"
            name="label"
            value={label}
            onChange={formik.handleChange}
            error={formik.touched.label && Boolean(formik.errors.label)}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={formik.touched.label && formik.errors.label}
          />
        </Grid>

        <Grid item xs={3} display={'flex'} alignItems={'center'}>
          <FormControlLabel
            disabled={disabled}
            control={<Checkbox />}
            label={`${t('forms.fieldAttributes.isRequired')}`}
            checked={formik.values.isRequired}
            onChange={() => {
              formik.setFieldValue('isRequired', !formik.values.isRequired);
            }}
          />
        </Grid>

        <Grid item xs={3} display={'flex'} alignItems={'center'}>
          {isRepeatingFieldsEnabled && (
            <FormControlLabel
              disabled={disabled}
              control={<Checkbox />}
              label={`${t('forms.fieldAttributes.isRepeatable')}`}
              checked={formik.values.isRepeatable}
              onChange={() => {
                formik.setFieldValue(
                  'isRepeatable',
                  !formik.values.isRepeatable,
                );
              }}
            />
          )}
        </Grid>

        <Grid item xs={6} display={'flex'} alignItems={'center'}>
          <FormControlLabel
            disabled={disabled}
            sx={{ ml: 0 }}
            control={<Checkbox />}
            label={`${t('forms.fieldAttributes.isMultiSelect')}`}
            checked={isMultiSelect}
            onChange={(e, checked) => {
              formik.setFieldValue('defaultValue', checked ? [] : null);
              formik.setFieldValue('isMultiSelect', checked);
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <AliasInput
            disabled={disabled}
            type="text"
            name="alias"
            value={alias}
            onChange={formik.handleChange}
            fullWidth
            error={formik.touched.alias && Boolean(formik.errors.alias)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <SelectDefaultValueField
            formik={formik}
            disabled={disabled}
            isRepeatingFieldsEnabled={isRepeatingFieldsEnabled}
          />
        </Grid>

        <Grid item xs={6}>
          <CodeLibraryAutocomplete formik={formik} disabled={disabled} />
        </Grid>
        <Grid item xs={6}>
          {!isCodeLibrary({
            sourceId: optionsSourceCodeLibraryId ?? '',
          }) && (
            <FormControlLabel
              disabled={disabled}
              sx={{ ml: 0 }}
              control={<Checkbox />}
              label={`${t('forms.fieldAttributes.dropdownList')}`}
              checked={component === SelectFieldDisplayComponent.Dropdown}
              onChange={(_e, checked) => {
                formik.setFieldValue(
                  'component',
                  checked
                    ? SelectFieldDisplayComponent.Dropdown
                    : SelectFieldDisplayComponent.Radio,
                );
              }}
            />
          )}
        </Grid>

        {optionsSourceCodeLibraryId && (
          <Grid item xs={12}>
            <Typography variant={'subtitle1'} ml={1} mb={2}>
              {getUsedByFieldsLabel({
                sourceId: optionsSourceCodeLibraryId ?? '',
                fieldId,
                fields: usedByFields,
              })}
            </Typography>
            <Divider />
          </Grid>
        )}

        <SelectOptionsEditor
          formik={formik}
          disabled={disabled}
          fieldId={fieldId}
        />

        <Grid item xs={12}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('forms.fieldAttributes.description')}
            type="text"
            name="description"
            value={description}
            onChange={formik.handleChange}
            multiline
            rows={3}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      </Grid>
    </form>
  );
}
